/**
 * @file Contractor-employee entities.
 *
 * The enums here are **not** uniformly backed by the database, and which kind
 * you are editing decides whether you need a migration:
 *
 * - {@link Gender} and {@link TrainingContractVariantType} are Postgres enums,
 *   mirrored by hand in `backend/lib/database-stack-v2/drizzle/schema/enums.ts`.
 *   Adding a member here is not enough: it needs `ALTER TYPE gender ADD VALUE`
 *   (or `training_contract_variant_type`) in a migration, or the value is
 *   rejected. Target the Postgres type name — the `pgEnum` first argument —
 *   not the `gender_type` variable that holds it.
 * - The other persisted enums (`employment_level`, `occupation_type`,
 *   `occupation`, `contract_type`, `employment_type`, `new_existing_job`,
 *   `social_factors`) are plain `text` columns. A new member needs no
 *   migration, and gets no database protection either: a typo persists
 *   silently and surfaces only where the value is switched on.
 * - `TrainingClassification` and `AgeGroup` are not persisted at all.
 *
 * For anything persisted, keep changes additive. Removing a member orphans
 * rows already carrying that string, which is a migration rather than an edit.
 */
import { z } from "zod";
export const DEFAULT_PAGINATION_LIMIT = 10;
export var Gender;
(function (Gender) {
    Gender["Male"] = "gender_male";
    Gender["Female"] = "gender_female";
    Gender["Other"] = "gender_other";
    Gender["Diverse"] = "gender_diverse";
})(Gender || (Gender = {}));
export var TrainingClassification;
(function (TrainingClassification) {
    TrainingClassification["NewEntrantOnSite"] = "training-classification_new-entrant-on-site";
    TrainingClassification["NewEntrantOffSite"] = "training-classification_new-entrant-off-site";
    TrainingClassification["NewEntrantSubcontractor"] = "training-classification_new-entrant-subcontractor";
    TrainingClassification["ExistingWorkerAccredited"] = "training-classification_existing-worker-accredited";
    TrainingClassification["ExistingWorkerNonAccredited"] = "training-classification_existing-worker-non-accredited";
})(TrainingClassification || (TrainingClassification = {}));
export const trainingClassificationToString = (trainingClassification) => {
    switch (trainingClassification) {
        case TrainingClassification.NewEntrantOnSite:
            return "New Entrant On-Site";
        case TrainingClassification.NewEntrantOffSite:
            return "New Entrant Off-Site";
        case TrainingClassification.NewEntrantSubcontractor:
            return "New Entrant Subcontractor";
        case TrainingClassification.ExistingWorkerAccredited:
            return "Existing Worker - Accredited";
        case TrainingClassification.ExistingWorkerNonAccredited:
            return "Existing Worker - Non-Accredited";
    }
};
export const stringImportToNewExistingJobType = (newExistingJobType) => {
    switch (newExistingJobType.toLowerCase()) {
        default:
            return undefined;
        case "new":
            return NewExistingJobType.New;
        case "existing":
            return NewExistingJobType.Existing;
    }
};
export var NewExistingJobType;
(function (NewExistingJobType) {
    NewExistingJobType["New"] = "new-existing-job-type_new";
    NewExistingJobType["Existing"] = "new-existing-job-type_existing";
})(NewExistingJobType || (NewExistingJobType = {}));
/**
 * Maps a cell from the customer employee-import spreadsheet to an
 * {@link OccupationType}. The lowercase literals below are the exact accepted
 * cell values, so they are a contract with the import template rather than
 * free-form labels — read by `xlsxEmployee.ts` and `employeeImportMapper.ts`
 * in the frontend's workforce surface.
 *
 * Two things that are not visible from the signature: "infrastructure spare
 * parts", "operations and maintenance" and "vehicle spare parts" all fold into
 * `TradeRole`, and anything unrecognised returns `undefined` rather than
 * throwing — which surfaces as a silently blank column on import.
 */
export const stringImportToOccupationType = (occupationType) => {
    switch (occupationType.toLowerCase()) {
        default:
            return undefined;
        case "trade role":
            return OccupationType.TradeRole;
        case "non-trade position":
            return OccupationType.NonTradePosition;
        case "management, supervisor & specialist labour roles":
            return OccupationType.ManagementSupervisorSpecialistLabourRoles;
        case "infrastructure spare parts":
        case "operations and maintenance":
        case "vehicle spare parts":
            return OccupationType.TradeRole;
        case "other":
            return OccupationType.Other;
    }
};
export var OccupationType;
(function (OccupationType) {
    OccupationType["TradeRole"] = "occupation-type-options_trade-role";
    OccupationType["NonTradePosition"] = "occupation-type-options_non-trade-position";
    OccupationType["ManagementSupervisorSpecialistLabourRoles"] = "occupation-type-options_management-supervisor-specialist";
    OccupationType["Other"] = "occupation-type-options_other";
})(OccupationType || (OccupationType = {}));
export var EmploymentLevel;
(function (EmploymentLevel) {
    EmploymentLevel["FullEmployment"] = "employment-level_full-employment";
    EmploymentLevel["Apprentice"] = "employment-level_apprentice";
    EmploymentLevel["Trainee"] = "employment-level_trainee";
    EmploymentLevel["Cadet"] = "employment-level_cadet";
    EmploymentLevel["Graduate"] = "employment-level_graduate";
    EmploymentLevel["Intern"] = "employment-level_intern";
    EmploymentLevel["Undergraduate"] = "employment-level_undergraduate";
    // South Australia
    EmploymentLevel["Upskilling"] = "employment-level_upskilling";
})(EmploymentLevel || (EmploymentLevel = {}));
export const ACT_EMPLOYMENT_LEVELS = [
    EmploymentLevel.Apprentice,
    EmploymentLevel.Trainee,
    EmploymentLevel.Cadet
];
export const stringImportToEmploymentType = (employmentType) => {
    switch (employmentType) {
        default:
            return undefined;
        case "Direct":
            return EmploymentType.Direct;
        case "Indirect":
            return EmploymentType.Indirect;
    }
};
export var EmploymentType;
(function (EmploymentType) {
    EmploymentType["Direct"] = "employment-type_direct";
    EmploymentType["Indirect"] = "employment-type_indirect";
})(EmploymentType || (EmploymentType = {}));
export var EmployeeSocialFactor;
(function (EmployeeSocialFactor) {
    EmployeeSocialFactor["AboriginalOrTorresStraitIslander"] = "employee-social-procurement-factor_aboriginal-or-torres-strait-islander";
    EmployeeSocialFactor["AsylumSeekerRefugee"] = "employee-social-procurement-factor_asylum-seeker-refugee";
    EmployeeSocialFactor["DisengagedYouth"] = "employee-social-procurement-factor_disengaged-youth";
    EmployeeSocialFactor["YoungPeopleAgedUnder25"] = "employee-social-procurement-factor_young-people-aged-under-25";
    EmployeeSocialFactor["LongTermUnemployed"] = "employee-social-procurement-factor_long-term-unemployed";
    EmployeeSocialFactor["PersonWithDisability"] = "employee-social-procurement-factor_person-with-disability-inclusive-of-mental-illness";
    EmployeeSocialFactor["Veteran"] = "employee-social-procurement-factor_veteran";
    EmployeeSocialFactor["PersonsAffectedByFamilyViolence"] = "employee-social-procurement-factor_person-affected-by-family-violence";
    EmployeeSocialFactor["ExOffender"] = "employee-social-procurement-factor_ex-offender";
    EmployeeSocialFactor["JobseekerWithAMentalIllness"] = "employee-social-procurement-factor_jobseeker-with-a-mental-illness";
    EmployeeSocialFactor["LivingInRegionsExperienceingEntrenchedDisadvantage"] = "employee-social-procurement-factor_living-in-regions-experiencing-entrenched-disadvantage";
    EmployeeSocialFactor["MatureAgedJobSeeker"] = "employee-social-procurement-factor_mature-aged-job-seeker";
    EmployeeSocialFactor["SingleParent"] = "employee-social-procurement-factor_single-parents";
    EmployeeSocialFactor["SocialHousingTenant"] = "employee-social-procurement-factor_social-housing-tenant";
    EmployeeSocialFactor["WorkersInTransitionRetrenchedWorked"] = "employee-social-procurement-factor_workers-in-transition-retrenched-workers";
    EmployeeSocialFactor["YoungPeopleInOutOfHomeCare"] = "employee-social-procurement-factor_young-people-in-out-of-home-care";
    EmployeeSocialFactor["WomenOver45"] = "employee-social-procurement-factor_women-over-45";
    EmployeeSocialFactor["CulturallyDiverseMigrant"] = "employee-social-procurement-factor_culturally-diverse-migrant";
    EmployeeSocialFactor["NewlyArrivedMigrantsFromNonEnglishSpeakingBackgrounds"] = "employee-social-procurement-factor_newly-arrived-migrants-from-non-english-speaking-backgrounds";
    EmployeeSocialFactor["JobSeekersRegisteredWithJobsVictoriaMentorService"] = "employee-social-procurement-factor_job-seekers-registered-with-jobs-victoria-mentor-service";
    EmployeeSocialFactor["GeespApprentice"] = "employee-social-procurement-factor_geesp-apprentice";
    // South Australia
    EmployeeSocialFactor["BarriersToEmployment"] = "employee-social-procurement-factor_barriers-to-employment";
    // Queensland
    EmployeeSocialFactor["CulturallyAndLinguisticallyDiverse"] = "employee-social-procurement-factor_culturally-and-linguistically-diverse";
    EmployeeSocialFactor["NewEntrantsToTheConstructionIndustry"] = "employee-social-procurement-factor_new-entrants-to-the-construction-industry";
    EmployeeSocialFactor["CaringResponsibilities"] = "employee-social-procurement-factor_caring-responsibilities";
    EmployeeSocialFactor["Lgbtqi"] = "employee-social-procurement-factor_lgbtqi";
})(EmployeeSocialFactor || (EmployeeSocialFactor = {}));
export var ContractType;
(function (ContractType) {
    ContractType["PermanentFullTime"] = "contract-type_permanent-full-time";
    ContractType["FixedTerm"] = "contract-type_fixed-term";
    ContractType["PartTime"] = "contract-type_part-time";
    ContractType["Casual"] = "contract-type_casual";
})(ContractType || (ContractType = {}));
export var TrainingContractVariantType;
(function (TrainingContractVariantType) {
    TrainingContractVariantType["StandardTrainingCourse"] = "training-contract-variant_standard-training-course";
    TrainingContractVariantType["ApprenticeTrainingContract"] = "training-contract-variant_apprentice-training-contract";
    TrainingContractVariantType["TraineeTrainingContract"] = "training-contract-variant_trainee-training-contract";
    TrainingContractVariantType["CadetTrainingCourse"] = "training-contract-variant_cadet-training-course";
    TrainingContractVariantType["UndergraduateTrainingCourse"] = "training-contract-variant_undergraduate-training-course";
    TrainingContractVariantType["GraduateTrainingCourse"] = "training-contract-variant_graduate-training-course";
})(TrainingContractVariantType || (TrainingContractVariantType = {}));
export var AgeGroup;
(function (AgeGroup) {
    AgeGroup["FifteenToTwentyFour"] = "age-group_15-24";
    AgeGroup["TwentyFiveToFourtyFour"] = "age-group_25-44";
    AgeGroup["FortyFiveToFourtyNine"] = "age-group_45-69";
    AgeGroup["FiftyPlus"] = "age-group_50+";
})(AgeGroup || (AgeGroup = {}));
export var Occupation;
(function (Occupation) {
    Occupation["AccountantGeneral"] = "occupation_accountant-general";
    Occupation["AccountantsAuditorsAndCompanySecretaries"] = "occupation_accountants-auditors-and-company-secretaries-nfd";
    Occupation["AccountsClerk"] = "occupation_accounts-clerk";
    Occupation["AdvertisingPublicRelationsAndSalesManagers"] = "occupation_advertising-public-relations-and-sales-managers-nfd";
    Occupation["AgriculturalEngineer"] = "occupation_agricultural-engineer";
    Occupation["AirconditioningAndMechanicalServicesPlumber"] = "occupation_airconditioning-and-mechanical-services-plumber";
    Occupation["AirconditioningAndRefrigerationMechanic"] = "occupation_airconditioning-and-refrigeration-mechanic";
    Occupation["Arborist"] = "occupation_arborist";
    Occupation["Architect"] = "occupation_architect";
    Occupation["ArchitectsAndLAndscapeArchitects"] = "occupation_architects-and-landscape-architects-nfd";
    Occupation["ArchitectsDesignersPlannersAndSurveyors"] = "occupation_architects-designers-planners-and-surveyors-nfd";
    Occupation["ArchitecturalDraftsperson"] = "occupation_architectural-draftsperson";
    Occupation["ArchitecturalBuildingAndSurveyingTechnicians"] = "occupation_architectural-building-and-surveying-technicians-nfd";
    Occupation["AuditorsCompanySecretariesAndCorporateTreasurers"] = "occupation_auditors-company-secretaries-and-corporate-treasurers-nfd";
    Occupation["AutomotiveElectrician"] = "occupation_automotive-electrician";
    Occupation["BackhoeOperator"] = "occupation_backhoe-operator";
    Occupation["BoilerorEngineOperator"] = "occupation_boiler-or-engine-operator";
    Occupation["BookKeeper"] = "occupation_bookkeeper";
    Occupation["Bricklayer"] = "occupation_bricklayer";
    Occupation["BuildersLabourer"] = "occupation_builders-labourer";
    Occupation["BuildingAndEngineeringTechnicians"] = "occupation_building-and-engineering-technicians-nfd";
    Occupation["BuildingAndPlumbingLabourers"] = "occupation_building-and-plumbing-labourers-nfd";
    Occupation["BuildingAssociate"] = "occupation_building-associate";
    Occupation["BuildingInspector"] = "occupation_building-inspector";
    Occupation["BuildingInsulationInstaller"] = "occupation_building-insulation-installer";
    Occupation["BulkMaterialsHAndlingPlantOperator"] = "occupation_bulk-materials-handling-plant-operator";
    Occupation["BulldozerOperator"] = "occupation_bulldozer-operator";
    Occupation["BusinessAdministrationManagers"] = "occupation_business-administration-managers-nfd";
    Occupation["BusinessHumanResourceAndMarketingProfessionals"] = "occupation_business-human-resource-and-marketing-professionals-nfd";
    Occupation["CabinetMaker"] = "occupation_cabinetmaker";
    Occupation["CablerDataAndTelecommunications"] = "occupation_cabler-data-and-telecommunications";
    Occupation["Carpenter"] = "occupation_carpenter";
    Occupation["CarpenterAndJoiner"] = "occupation_carpenter-and-joiner";
    Occupation["CementProductionPlantOperator"] = "occupation_cement-production-plant-operator";
    Occupation["ChemicalAndMaterialsEngineers"] = "occupation_chemical-and-materials-engineers-nfd";
    Occupation["ChemicalEngineer"] = "occupation_chemical-engineer";
    Occupation["ChiefExecutiveorManagingDirector"] = "occupation_chief-executive-or-managing-director";
    Occupation["ChiefExecutivesGeneralManagersAndLegislators"] = "occupation_chief-executives-general-managers-and-legislators-nfd";
    Occupation["CivilEngineer"] = "occupation_civil-engineer";
    Occupation["CivilEngineeringDraftsperson"] = "occupation_civil-engineering-draftsperson";
    Occupation["CivilEngineeringProfessionals"] = "occupation_civil-engineering-professionals-nfd";
    Occupation["CivilEngineeringTechnician"] = "occupation_civil-engineering-technician";
    Occupation["ClayProductsMachineOperator"] = "occupation_clay-products-machine-operator";
    Occupation["ClayConcreteGlassAndStoneProcessingMachineOperators"] = "occupation_clay-concrete-glass-and-stone-processing-machine-operators-nfd";
    Occupation["ClericalAndAdministrativeWorkers"] = "occupation_clerical-and-administrative-workers-nfd";
    Occupation["ClericalAndOfficeSupportWorkers"] = "occupation_clerical-and-office-support-workers-nfd";
    Occupation["CommercialCleaner"] = "occupation_commercial-cleaner";
    Occupation["ConcreteBatchingPlantOperator"] = "occupation_concrete-batching-plant-operator";
    Occupation["ConcreteProductsMachineOperator"] = "occupation_concrete-products-machine-operator";
    Occupation["ConcretePumpOperator"] = "occupation_concrete-pump-operator";
    Occupation["Concreter"] = "occupation_concreter";
    Occupation["ConstructionAndMiningLabourers"] = "occupation_construction-and-mining-labourers-nfd";
    Occupation["ConstructionEstimator"] = "occupation_construction-estimator";
    Occupation["ConstructionManagers"] = "occupation_construction-managers-nfd";
    Occupation["ConstructionProjectManager"] = "occupation_construction-project-manager";
    Occupation["ConstructionRigger"] = "occupation_construction-rigger";
    Occupation["ConstructionTradesWorkers"] = "occupation_construction-trades-workers-nfd";
    Occupation["ConstructionDistributionAndProductionManagers"] = "occupation_construction-distribution-and-production-managers-nfd";
    Occupation["ContractAdministrator"] = "occupation_contract-administrator";
    Occupation["CorporateGeneralManager"] = "occupation_corporate-general-manager";
    Occupation["CorporateServicesManager"] = "occupation_corporate-services-manager";
    Occupation["CraneChaser"] = "occupation_crane-chaser";
    Occupation["CraneHoistorLiftOperator"] = "occupation_crane-hoist-or-lift-operator";
    Occupation["DataEntryOperator"] = "occupation_data-entry-operator";
    Occupation["DesignEngineeringScienceAndTransportProfessionals"] = "occupation_design-engineering-science-and-transport-professionals-nfd";
    Occupation["DieselMotorMechanic"] = "occupation_diesel-motor-mechanic";
    Occupation["DrainageSewerageAndStormwaterLabourer"] = "occupation_drainage-sewerage-and-stormwater-labourer";
    Occupation["Drainer"] = "occupation_drainer";
    Occupation["Driller"] = "occupation_driller";
    Occupation["DrillersAssistant"] = "occupation_drillers-assistant";
    Occupation["EarthmovingLabourer"] = "occupation_earthmoving-labourer";
    Occupation["EarthmovingPlantOperatorGeneral"] = "occupation_earthmoving-plant-operator-general";
    Occupation["ElectricalDistributionTradesWorkers"] = "occupation_electrical-distribution-trades-workers-nfd";
    Occupation["ElectricalEngineer"] = "occupation_electrical-engineer";
    Occupation["ElectricalEngineeringDraftsperson"] = "occupation_electrical-engineering-draftsperson";
    Occupation["ElectricalEngineeringTechnician"] = "occupation_electrical-engineering-technician";
    Occupation["ElectricalLinesworker"] = "occupation_electrical-linesworker";
    Occupation["ElectricianGeneral"] = "occupation_electrician-general";
    Occupation["ElectricianSpecialClass"] = "occupation_electrician-special-class";
    Occupation["ElectronicEngineeringDraftsperson"] = "occupation_electronic-engineering-draftsperson";
    Occupation["ElectronicEngineeringTechnician"] = "occupation_electronic-engineering-technician";
    Occupation["ElectronicEquipmentTradesWorker"] = "occupation_electronic-equipment-trades-worker";
    Occupation["ElectronicInstrumentTradesWorkerGeneral"] = "occupation_electronic-instrument-trades-worker-general";
    Occupation["ElectronicsEngineer"] = "occupation_electronics-engineer";
    Occupation["ElectronicsTradesWorkers"] = "occupation_electronics-trades-workers-nfd";
    Occupation["ElectrotechnologyAndTelecommunicationsTradesWorkers"] = "occupation_electrotechnology-and-telecommunications-trades-workers-nfd";
    Occupation["EngineeringManager"] = "occupation_engineering-manager";
    Occupation["EngineeringProductionWorker"] = "occupation_engineering-production-worker";
    Occupation["EngineeringProfessionals"] = "occupation_engineering-professionals-nfd";
    Occupation["EngineeringTechnologist"] = "occupation_engineering-technologist";
    Occupation["EngineeringICTAndScienceTechnicians"] = "occupation_engineering-ict-and-science-technicians-nfd";
    Occupation["EnvironmentalConsultant"] = "occupation_environmental-consultant";
    Occupation["EnvironmentalEngineer"] = "occupation_environmental-engineer";
    Occupation["EnvironmentalManager"] = "occupation_environmental-manager";
    Occupation["EnvironmentalResearchScientist"] = "occupation_environmental-research-scientist";
    Occupation["ExcavatorOperator"] = "occupation_excavator-operator";
    Occupation["FacilitiesAdministrator"] = "occupation_facilities-administrator";
    Occupation["Fencer"] = "occupation_fencer";
    Occupation["FibrousPlasterer"] = "occupation_fibrous-plasterer";
    Occupation["FinanceManager"] = "occupation_finance-manager";
    Occupation["FireProtectionEquipmentTechnician"] = "occupation_fire-protection-equipment-technician";
    Occupation["FitterGeneral"] = "occupation_fitter-general";
    Occupation["FitterAndTurner"] = "occupation_fitter-and-turner";
    Occupation["FitterWelder"] = "occupation_fitter-welder";
    Occupation["FloorFinisher"] = "occupation_floor-finisher";
    Occupation["ForkliftDriver"] = "occupation_forklift-driver";
    Occupation["GardenerGeneral"] = "occupation_gardener-general";
    Occupation["Gasfitter"] = "occupation_gasfitter";
    Occupation["GeneralManagers"] = "occupation_general-managers-nfd";
    Occupation["Geologist"] = "occupation_geologist";
    Occupation["GeotechnicalEngineer"] = "occupation_geotechnical-engineer";
    Occupation["GlassProductionMachineOperator"] = "occupation_glass-production-machine-operator";
    Occupation["Glazier"] = "occupation_glazier";
    Occupation["GraderOperator"] = "occupation_grader-operator";
    Occupation["HumanResourceAdviser"] = "occupation_human-resource-adviser";
    Occupation["HumanResourceAndTrainingProfessionals"] = "occupation_human-resource-and-training-professionals-nfd";
    Occupation["HumanResourceClerk"] = "occupation_human-resource-clerk";
    Occupation["HumanResourceManager"] = "occupation_human-resource-manager";
    Occupation["ICTBusinessAndSystemsAnalysts"] = "occupation_ict-business-and-systems-analysts-nfd";
    Occupation["ICTManagers"] = "occupation_ict-managers-nfd";
    Occupation["ICTProfessionals"] = "occupation_ict-professionals-nfd";
    Occupation["ICTSecuritySpecialist"] = "occupation_ict-security-specialist";
    Occupation["ICTSupportAndTestEngineers"] = "occupation_ict-support-and-test-engineers-nfd";
    Occupation["IndustrialEngineer"] = "occupation_industrial-engineer";
    Occupation["IndustrialSpraypainter"] = "occupation_industrial-spraypainter";
    Occupation["IndustrialMechanicalAndProductionEngineers"] = "occupation_industrial-mechanical-and-production-engineers-nfd";
    Occupation["InspectorsAndRegulatoryOfficers"] = "occupation_inspectors-and-regulatory-officers-nfd";
    Occupation["InteriorDesigner"] = "occupation_interior-designer";
    Occupation["Joiner"] = "occupation_joiner";
    Occupation["Labourers"] = "occupation_labourers-nfd";
    Occupation["Lagger"] = "occupation_lagger";
    Occupation["LandscapeArchitect"] = "occupation_landscape-architect";
    Occupation["LiftMechanic"] = "occupation_lift-mechanic";
    Occupation["Linemarker"] = "occupation_linemarker";
    Occupation["LoaderOperator"] = "occupation_loader-operator";
    Occupation["Locksmith"] = "occupation_locksmith";
    Occupation["LogisticsClerks"] = "occupation_logistics-clerks-nfd";
    Occupation["MachineAndStationaryPlantOperators"] = "occupation_machine-and-stationary-plant-operators-nfd";
    Occupation["MachineryOperatorsAndDrivers"] = "occupation_machinery-operators-and-drivers-nfd";
    Occupation["ManagementAccountant"] = "occupation_management-accountant";
    Occupation["Managers"] = "occupation_managers-nfd";
    Occupation["MaterialsEngineer"] = "occupation_materials-engineer";
    Occupation["MechanicalEngineer"] = "occupation_mechanical-engineer";
    Occupation["MechanicalEngineeringDraftsperson"] = "occupation_mechanical-engineering-draftsperson";
    Occupation["MechanicalEngineeringTechnician"] = "occupation_mechanical-engineering-technician";
    Occupation["MechanicalEngineeringTradesWorkers"] = "occupation_mechanical-engineering-trades-workers-nfd";
    Occupation["MediaProfessionals"] = "occupation_media-professionals-nfd";
    Occupation["MetalCastingTradesWorker"] = "occupation_metal-casting-trades-worker";
    Occupation["MetalCastingForgingAndFinishingTradesWorkers"] = "occupation_metal-casting-forging-and-finishing-trades-workers-nfd";
    Occupation["MetalEngineeringProcessWorker"] = "occupation_metal-engineering-process-worker";
    Occupation["MetalFabricator"] = "occupation_metal-fabricator";
    Occupation["MetalFittersAndMachinists"] = "occupation_metal-fitters-and-machinists-nfd";
    Occupation["MetalMachinistFirstClass"] = "occupation_metal-machinist-first-class";
    Occupation["MetalPolisher"] = "occupation_metal-polisher";
    Occupation["MobilePlantOperators"] = "occupation_mobile-plant-operators-nfd";
    Occupation["MotorMechanicGeneral"] = "occupation_motor-mechanic-general";
    Occupation["MultimediaDesigner"] = "occupation_multimedia-designer";
    Occupation["MultimediaSpecialistsAndWebDevelopers"] = "occupation_multimedia-specialists-and-web-developers-nfd";
    Occupation["OccupationalHealthAndSafetyAdviser"] = "occupation_occupational-health-and-safety-adviser";
    Occupation["OtherSpatialScientist"] = "occupation_other-spatial-scientist";
    Occupation["OtherConstructionAndMiningLabourers"] = "occupation_other-construction-and-mining-labourers-nfd";
    Occupation["OtherWoodProcessingMachineOperator"] = "occupation_other-wood-processing-machine-operator";
    Occupation["PaintingTradesWorker"] = "occupation_painting-trades-worker";
    Occupation["PavingAndSurfacingLabourer"] = "occupation_paving-and-surfacing-labourer";
    Occupation["PavingPlantOperator"] = "occupation_paving-plant-operator";
    Occupation["PayrollClerk"] = "occupation_payroll-clerk";
    Occupation["Plasterers"] = "occupation_plasterers-nfd";
    Occupation["PlasticsTechnician"] = "occupation_plastics-technician";
    Occupation["PlumberGeneral"] = "occupation_plumber-general";
    Occupation["PlumbersAssistant"] = "occupation_plumbers-assistant";
    Occupation["PlumbingInspector"] = "occupation_plumbing-inspector";
    Occupation["PolicyAndPlanningManager"] = "occupation_policy-and-planning-manager";
    Occupation["PrecisionMetalTradesWorkers"] = "occupation_precision-metal-trades-workers-nfd";
    Occupation["PressureWelder"] = "occupation_pressure-welder";
    Occupation["Professionals"] = "occupation_professionals-nfd";
    Occupation["ProgramOrProjectAdministrator"] = "occupation_program-or-project-administrator";
    Occupation["ProjectBuilder"] = "occupation_project-builder";
    Occupation["PublicRelationsManager"] = "occupation_public-relations-manager";
    Occupation["PublicRelationsProfessional"] = "occupation_public-relations-professional";
    Occupation["PurchasingAndSupplyLogisticsClerks"] = "occupation_purchasing-and-supply-logistics-clerks-nfd";
    Occupation["PurchasingOfficer"] = "occupation_purchasing-officer";
    Occupation["QualityAssuranceManager"] = "occupation_quality-assurance-manager";
    Occupation["QuantitySurveyor"] = "occupation_quantity-surveyor";
    Occupation["RailwaySignalOperator"] = "occupation_railway-signal-operator";
    Occupation["RailwayTrackPlantOperator"] = "occupation_railway-track-plant-operator";
    Occupation["RailwayTrackWorker"] = "occupation_railway-track-worker";
    Occupation["RecruitmentConsultant"] = "occupation_recruitment-consultant";
    Occupation["ResearchAndDevelopmentManager"] = "occupation_research-and-development-manager";
    Occupation["RoadRollerOperator"] = "occupation_road-roller-operator";
    Occupation["RoadTrafficController"] = "occupation_road-traffic-controller";
    Occupation["RoofPlumber"] = "occupation_roof-plumber";
    Occupation["RoofTiler"] = "occupation_roof-tiler";
    Occupation["SafetyInspector"] = "occupation_safety-inspector";
    Occupation["SalesMarketingAndPublicRelationsProfessionals"] = "occupation_sales-marketing-and-public-relations-professionals-nfd";
    Occupation["SandBlaster"] = "occupation_sand-blaster";
    Occupation["SawMillOrTimberYardWorker"] = "occupation_sawmill-or-timber-yard-worker";
    Occupation["SawMillingOperator"] = "occupation_sawmilling-operator";
    Occupation["Scaffolder"] = "occupation_scaffolder";
    Occupation["SecretaryGeneral"] = "occupation_secretary-general";
    Occupation["SecurityOfficer"] = "occupation_security-officer";
    Occupation["SheetmetalTradesWorker"] = "occupation_sheetmetal-trades-worker";
    Occupation["SignErector"] = "occupation_sign-erector";
    Occupation["SoftwareAndApplicationsProgrammers"] = "occupation_software-and-applications-programmers-nfd";
    Occupation["Solicitor"] = "occupation_solicitor";
    Occupation["SolidPlasterer"] = "occupation_solid-plasterer";
    Occupation["SpecialistManagers"] = "occupation_specialist-managers-nfd";
    Occupation["StationaryPlantOperators"] = "occupation_stationary-plant-operators-nfd";
    Occupation["SteelFixer"] = "occupation_steel-fixer";
    Occupation["StoneProcessingMachineOperator"] = "occupation_stone-processing-machine-operator";
    Occupation["Stonemason"] = "occupation_stonemason";
    Occupation["StreetSweeperOperator"] = "occupation_streetsweeper-operator";
    Occupation["StructuralEngineer"] = "occupation_structural-engineer";
    Occupation["StructuralSteelAndWeldingTradesWorkers"] = "occupation_structural-steel-and-welding-trades-workers-nfd";
    Occupation["StructuralSteelConstructionWorkers"] = "occupation_structural-steel-construction-workers-nfd";
    Occupation["StructuralSteelErector"] = "occupation_structural-steel-erector";
    Occupation["SupplyAndDistributionManager"] = "occupation_supply-and-distribution-manager";
    Occupation["SurveyingorSpatialScienceTechnician"] = "occupation_surveying-or-spatial-science-technician";
    Occupation["Surveyor"] = "occupation_surveyor";
    Occupation["SurveyorsAssistant"] = "occupation_surveyors-assistant";
    Occupation["TankerDriver"] = "occupation_tanker-driver";
    Occupation["TaxationAccountant"] = "occupation_taxation-accountant";
    Occupation["TechnicalCableJointer"] = "occupation_technical-cable-jointer";
    Occupation["TechniciansAndTradesWorkers"] = "occupation_technicians-and-trades-workers-nfd";
    Occupation["TelecommunicationsCableJointer"] = "occupation_telecommunications-cable-jointer";
    Occupation["TelecommunicationsEngineer"] = "occupation_telecommunications-engineer";
    Occupation["TelecommunicationsEngineeringProfessionals"] = "occupation_telecommunications-engineering-professionals-nfd";
    Occupation["TelecommunicationsLinesworker"] = "occupation_telecommunications-linesworker";
    Occupation["TelecommunicationsNetworkEngineer"] = "occupation_telecommunications-network-engineer";
    Occupation["TowTruckDriver"] = "occupation_tow-truck-driver";
    Occupation["TransportEngineer"] = "occupation_transport-engineer";
    Occupation["TruckDriverGeneral"] = "occupation_truck-driver-general";
    Occupation["UrbanAndRegionalPlanner"] = "occupation_urban-and-regional-planner";
    Occupation["WallAndFloorTiler"] = "occupation_wall-and-floor-tiler";
    Occupation["WelderFirstClass"] = "occupation_welder-first-class";
    Occupation["WoodAndWoodProductsFactoryWorker"] = "occupation_wood-and-wood-products-factory-worker";
    Occupation["WoodTradesWorkers"] = "occupation_wood-trades-workers-nfd";
    Occupation["WorkplaceRelationsAdviser"] = "occupation_workplace-relations-adviser";
    Occupation["InfrastructureSpareParts"] = "occupation_infrastructure-spare-parts";
    Occupation["OperationsAndMaintenance"] = "occupation_operations-and-maintenance";
    Occupation["VehicleSpareParts"] = "occupation_vehicle-spare-parts";
})(Occupation || (Occupation = {}));
export const supportingDocumentSchema = z.object({
    comment: z.string().optional(),
    contractor_employee_id: z.string(),
    created: z.date(),
    document_category: z.string().optional(),
    document_description: z.string().optional(),
    supporting_document_id: z.string(),
    supporting_document_type: z.string(),
    uri: z.string()
});
export const contractorEmployeeTrainingContractSchema = z.object({
    contractor_employee_id: z.string(),
    contractor_employee_training_contract_id: z.string(),
    course_name: z.string(),
    created: z.date(),
    end_date: z.date(),
    institution_name: z.string(),
    new_entrant_to_construction: z.boolean(),
    placement_hours_required: z.number().optional(),
    start_date: z.date().optional(),
    supporting_documents: z.array(supportingDocumentSchema),
    trainee_contract_number: z.string().optional(),
    unique_student_id: z.string().optional(),
    variant_type: z.enum(TrainingContractVariantType)
});
export const workPackageInfoSchema = z.object({
    contractor_name: z.string(),
    contractor_package_id: z.string()
});
export const contractorEmployeeProjectAssignmentSchema = z.object({
    assigned: z.boolean(),
    contractor_employee_id: z.string(),
    contractor_employee_project_id: z.string(),
    contractor_package_id: z.string(),
    created: z.union([z.date(), z.string()]),
    project_id: z.string(),
    project_name: z.string()
});
export const contractorEmployeeSchema = z.object({
    archived: z.boolean(),
    assigned: z.boolean().optional(),
    backfill_hours: z.boolean().optional(),
    contract_type: z.enum(ContractType).optional(),
    contractor_employee_id: z.string(),
    contractor_id: z
        .string()
        .min(1)
        .describe("Contractor the employee belongs to"),
    country: z.string().min(1).describe("Employee country"),
    created: z.date(),
    dob: z.string().optional(),
    employee_code: z.string().min(1).describe("Employee code"),
    employee_identifier: z
        .string()
        .optional()
        .describe("Optional employee identifier or email"),
    employer_abn: z.string().min(1).describe("Primary employer ABN"),
    employment_level: z.enum(EmploymentLevel).describe("Employment level"),
    employment_type: z.enum(EmploymentType).describe("Employment type"),
    gender: z.enum(Gender).describe("Employee gender"),
    geo_location_lat: z.number().optional(),
    geo_location_long: z.number().optional(),
    hammertech_id: z.string().optional(),
    hammertech_setup_approved: z.boolean().optional(),
    hammertech_sync_timestamp: z.date().optional(),
    hourly_rate: z.number().optional(),
    new_existing_job: z
        .enum(NewExistingJobType)
        .describe("New or existing job type"),
    occupation: z.enum(Occupation).describe("Employee occupation"),
    occupation_type: z.enum(OccupationType).describe("Occupation type"),
    post_code: z.string().optional().describe("Post code"),
    primary_employer: z.string().min(1).describe("Primary employer name"),
    social_factors: z
        .array(z.enum(EmployeeSocialFactor))
        .describe("Applicable social factors"),
    supporting_documents: z.array(supportingDocumentSchema),
    training_contracts: z
        .array(contractorEmployeeTrainingContractSchema)
        .optional(),
    work_packages: z.array(workPackageInfoSchema).optional()
});
export const createContractorEmployeeSchema = contractorEmployeeSchema
    .pick({
    contractor_id: true,
    country: true,
    employee_code: true,
    employee_identifier: true,
    employer_abn: true,
    employment_level: true,
    employment_type: true,
    gender: true,
    new_existing_job: true,
    occupation: true,
    occupation_type: true,
    post_code: true,
    primary_employer: true,
    social_factors: true
})
    .extend({
    social_factors: z
        .array(z.enum(EmployeeSocialFactor))
        .default([])
        .describe("Applicable social factors")
});
